import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  PencilLine,
} from 'lucide-react';
import { bdeseService } from '../services/bdese.service';
import type {
  CanalInformationBdese,
  EtatModalitesBdese,
  EtatInformationsBdese,
  FixerModalitesBdese,
  SupportBdese,
} from '../types';

const LIBELLE_CANAL: Record<CanalInformationBdese, string> = {
  portail: 'Portail',
  courriel: 'Courriel',
  portail_et_courriel: 'Portail et courriel',
};

const DESCRIPTION_CANAL: Record<CanalInformationBdese, string> = {
  portail: "Notification de chaque actualisation dans le portail Innov'Paie.",
  courriel: 'Courriel envoyé aux bénéficiaires lors de chaque actualisation.',
  portail_et_courriel:
    "Notification dans le portail Innov'Paie et courriel envoyé aux bénéficiaires lors de chaque actualisation.",
};

const FORMULAIRE_VIDE: FixerModalitesBdese = {
  support: 'informatique',
  canalInformation: 'portail',
  modalitesAcces: '',
  modalitesInformation: DESCRIPTION_CANAL.portail,
  effetUtileAtteste: false,
};

function messageErreur(erreur: unknown): string {
  const reponse = erreur as {
    response?: { status?: number; data?: { message?: string | string[] } };
  };
  if (reponse.response?.status === 401 || reponse.response?.status === 403) {
    return "Vous n'avez pas accès aux modalités de cette BDESE.";
  }
  const message = reponse.response?.data?.message;
  return Array.isArray(message)
    ? message.join(' ')
    : message || 'Les modalités ne peuvent pas être chargées pour le moment.';
}

interface BdeseModalitesPanelProps {
  folderUuid: string;
  /** Réutilise la décision serveur pour masquer les autres actions d'écriture. */
  onModifiableChange?: (modifiable: boolean | null) => void;
}

/**
 * Un panneau secondaire dans le parcours BDESE existant : aucune nouvelle
 * destination ni nouvelle carte de pilotage. Le CSE lit ; l'employeur fixe.
 */
export function BdeseModalitesPanel({
  folderUuid,
  onModifiableChange,
}: BdeseModalitesPanelProps) {
  const [etat, setEtat] = useState<EtatModalitesBdese | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [modifiable, setModifiable] = useState(false);
  const [ouvert, setOuvert] = useState(true);
  const [edition, setEdition] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [erreurFormulaire, setErreurFormulaire] = useState<string | null>(null);
  const [formulaire, setFormulaire] =
    useState<FixerModalitesBdese>(FORMULAIRE_VIDE);
  const [informations, setInformations] =
    useState<EtatInformationsBdese | null>(null);
  const [chargementInformations, setChargementInformations] = useState(false);
  const [erreurInformations, setErreurInformations] = useState(false);
  const [repriseEnCours, setRepriseEnCours] = useState(false);
  const [confirmationReprise, setConfirmationReprise] = useState(false);

  const chargerInformations = useCallback(async () => {
    setChargementInformations(true);
    setErreurInformations(false);
    try {
      setInformations(await bdeseService.lireInformations(folderUuid));
    } catch {
      setInformations(null);
      setErreurInformations(true);
    } finally {
      setChargementInformations(false);
    }
  }, [folderUuid]);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    setConfirmation(null);
    setConfirmationReprise(false);
    onModifiableChange?.(null);

    try {
      const modalites = await bdeseService.lireModalites(folderUuid);
      setEtat(modalites);
      setModifiable(modalites.modifiable);
      onModifiableChange?.(modalites.modifiable);
      setOuvert(!modalites.modalites || !modalites.conformite.conforme);
      if (modalites.modifiable) {
        void chargerInformations();
      } else {
        setInformations(null);
        setErreurInformations(false);
        setChargementInformations(false);
      }
    } catch (cause) {
      setEtat(null);
      setModifiable(false);
      onModifiableChange?.(null);
      setErreur(messageErreur(cause));
    } finally {
      setChargement(false);
    }
  }, [chargerInformations, folderUuid, onModifiableChange]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const manquementsBloquants = useMemo(
    () =>
      etat?.conformite.manquements.filter(
        manquement => manquement.gravite === 'bloquant'
      ) ?? [],
    [etat]
  );

  const commencerEdition = () => {
    const existant = etat?.modalites;
    setFormulaire(
      existant
        ? {
            support: existant.support,
            canalInformation: existant.canalInformation ?? 'portail',
            modalitesAcces: existant.modalitesAcces ?? '',
            modalitesInformation:
              existant.modalitesInformation ??
              DESCRIPTION_CANAL[existant.canalInformation ?? 'portail'],
            effetUtileAtteste: existant.effetUtileAtteste,
          }
        : {
            ...FORMULAIRE_VIDE,
            support: 'informatique',
          }
    );
    setErreurFormulaire(null);
    setConfirmation(null);
    setEdition(true);
    setOuvert(true);
  };

  const changerCanal = (canalInformation: CanalInformationBdese) => {
    setFormulaire(courant => ({
      ...courant,
      canalInformation,
      modalitesInformation: DESCRIPTION_CANAL[canalInformation],
    }));
  };

  const enregistrer = async () => {
    if (!formulaire.modalitesAcces.trim()) {
      setErreurFormulaire(
        "Décrivez les modalités d'accès, de consultation et d'utilisation."
      );
      return;
    }

    setEnregistrement(true);
    setErreurFormulaire(null);
    try {
      const resultat = await bdeseService.fixerModalites(folderUuid, {
        ...formulaire,
        modalitesAcces: formulaire.modalitesAcces.trim(),
      });
      setEtat(resultat);
      setModifiable(resultat.modifiable);
      onModifiableChange?.(resultat.modifiable);
      setEdition(false);
      setOuvert(true);
      setConfirmation('Modalités enregistrées.');
    } catch (e) {
      setErreurFormulaire(messageErreur(e));
    } finally {
      setEnregistrement(false);
    }
  };

  const statut = chargement
    ? 'Chargement…'
    : !etat?.modalites
      ? 'À définir'
      : manquementsBloquants.length > 0
        ? `${manquementsBloquants.length} point${manquementsBloquants.length > 1 ? 's' : ''} à corriger`
        : 'Définies';

  const informationsEnAttente =
    informations?.informations.filter(information =>
      ['en_attente', 'en_cours'].includes(information.statut)
    ).length ?? 0;
  const informationsEnEchec =
    informations?.informations.filter(
      information => information.statut === 'echec'
    ).length ?? 0;
  const aUnIncidentInformation =
    informationsEnAttente > 0 || informationsEnEchec > 0;

  const reprendreInformations = async () => {
    setRepriseEnCours(true);
    setConfirmationReprise(false);
    try {
      await bdeseService.reprendreInformations(folderUuid);
      setConfirmationReprise(true);
      await chargerInformations();
    } catch {
      setErreurInformations(true);
    } finally {
      setRepriseEnCours(false);
    }
  };

  return (
    <section className="shadow-soft mt-5 overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <details
        open={ouvert}
        onToggle={event => setOuvert(event.currentTarget.open)}
        className="group"
      >
        <summary className="focus-visible:ring-primary-600 flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 px-5 py-3 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset [&::-webkit-details-marker]:hidden">
          <span>
            <span className="block text-sm font-medium text-neutral-900">
              Modalités d&apos;accès et d&apos;information
            </span>
          </span>
          <span className="flex items-center gap-3">
            <span className="text-xs font-medium text-neutral-600">
              {statut}
            </span>
            <ChevronDown
              className="h-4 w-4 text-neutral-400 transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </span>
        </summary>

        <div className="border-t border-neutral-100 px-5 py-5">
          {chargement ? (
            <p className="text-sm text-neutral-500" role="status">
              Chargement des modalités…
            </p>
          ) : erreur ? (
            <div role="alert" className="flex items-start gap-3">
              <AlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-red-600"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm text-red-800">{erreur}</p>
                <button
                  type="button"
                  onClick={() => void charger()}
                  className="focus-visible:ring-primary-600 mt-3 min-h-11 rounded-lg px-3 text-sm font-medium text-neutral-700 underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950 focus-visible:ring-2 focus-visible:outline-none"
                >
                  Réessayer
                </button>
              </div>
            </div>
          ) : edition ? (
            <form
              className="max-w-2xl space-y-5"
              onSubmit={event => {
                event.preventDefault();
                void enregistrer();
              }}
            >
              <div>
                <label
                  htmlFor="bdese-support"
                  className="mb-1.5 block text-sm font-medium text-neutral-800"
                >
                  Support de mise à disposition
                </label>
                <select
                  id="bdese-support"
                  value={formulaire.support}
                  disabled={etat?.conformite.supportImpose === 'informatique'}
                  onChange={event =>
                    setFormulaire(courant => ({
                      ...courant,
                      support: event.target.value as SupportBdese,
                    }))
                  }
                  className="focus:border-primary-600 focus:ring-primary-600/20 min-h-11 w-full rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:ring-2 focus:outline-none disabled:bg-neutral-50 disabled:text-neutral-600 sm:max-w-sm"
                >
                  <option value="informatique">Informatique</option>
                  <option value="papier">Papier</option>
                </select>
                {etat?.conformite.supportImpose === 'informatique' && (
                  <p className="mt-1.5 text-xs text-neutral-500">
                    Le support informatique est imposé par l&apos;effectif
                    retenu.
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="bdese-canal"
                  className="mb-1.5 block text-sm font-medium text-neutral-800"
                >
                  Information des bénéficiaires
                </label>
                <select
                  id="bdese-canal"
                  value={formulaire.canalInformation}
                  onChange={event =>
                    changerCanal(event.target.value as CanalInformationBdese)
                  }
                  className="focus:border-primary-600 focus:ring-primary-600/20 min-h-11 w-full rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:ring-2 focus:outline-none sm:max-w-sm"
                >
                  <option value="portail">Portail (recommandé)</option>
                  <option value="courriel">Courriel</option>
                  <option value="portail_et_courriel">Les deux</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="bdese-acces"
                  className="mb-1.5 block text-sm font-medium text-neutral-800"
                >
                  Accès, consultation et utilisation
                </label>
                <textarea
                  id="bdese-acces"
                  rows={4}
                  maxLength={3000}
                  value={formulaire.modalitesAcces}
                  onChange={event =>
                    setFormulaire(courant => ({
                      ...courant,
                      modalitesAcces: event.target.value,
                    }))
                  }
                  placeholder="Décrivez comment les bénéficiaires accèdent à la base et peuvent l’utiliser"
                  className="focus:border-primary-600 focus:ring-primary-600/20 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400/50 focus:ring-2 focus:outline-none"
                />
              </div>

              <label className="flex max-w-2xl items-start gap-3 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={formulaire.effetUtileAtteste}
                  onChange={event =>
                    setFormulaire(courant => ({
                      ...courant,
                      effetUtileAtteste: event.target.checked,
                    }))
                  }
                  className="text-primary-600 focus:ring-primary-600 mt-1 h-4 w-4 rounded border-neutral-300"
                />
                <span>
                  J&apos;atteste que ces modalités permettent aux bénéficiaires
                  d&apos;exercer utilement leurs compétences.
                </span>
              </label>

              {erreurFormulaire && (
                <p className="text-sm text-red-800" role="alert">
                  {erreurFormulaire}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={enregistrement}
                  className="focus-visible:ring-primary-600 min-h-11 rounded-lg px-4 text-sm font-medium transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-40"
                  style={{
                    backgroundColor: 'var(--marque-accent)',
                    color: 'var(--marque-sur-accent)',
                  }}
                >
                  {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button
                  type="button"
                  disabled={enregistrement}
                  onClick={() => setEdition(false)}
                  className="focus-visible:ring-primary-600 min-h-11 rounded-lg px-4 text-sm font-medium text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:ring-2 focus-visible:outline-none disabled:opacity-40"
                >
                  Annuler
                </button>
              </div>
            </form>
          ) : (
            <div className="max-w-2xl">
              {confirmation && (
                <p
                  role="status"
                  className="mb-4 flex items-center gap-2 text-sm text-emerald-700"
                >
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  {confirmation}
                </p>
              )}

              {etat?.modalites ? (
                <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[11rem_1fr]">
                  <dt className="text-neutral-500">Support</dt>
                  <dd className="text-neutral-900">
                    {etat.modalites.support === 'informatique'
                      ? 'Informatique'
                      : 'Papier'}
                  </dd>
                  <dt className="text-neutral-500">Information</dt>
                  <dd className="text-neutral-900">
                    <span className="block">
                      {etat.modalites.canalInformation
                        ? LIBELLE_CANAL[etat.modalites.canalInformation]
                        : 'Canal non déterminé'}
                    </span>
                    {etat.modalites.modalitesInformation && (
                      <span className="mt-0.5 block text-neutral-600">
                        {etat.modalites.modalitesInformation}
                      </span>
                    )}
                  </dd>
                  <dt className="text-neutral-500">Modalités d&apos;accès</dt>
                  <dd className="whitespace-pre-wrap text-neutral-900">
                    {etat.modalites.modalitesAcces || 'Non renseignées'}
                  </dd>
                  <dt className="text-neutral-500">Effet utile</dt>
                  <dd className="text-neutral-900">
                    {etat.modalites.effetUtileAtteste
                      ? 'Attesté par l’employeur'
                      : 'Non attesté'}
                  </dd>
                </dl>
              ) : (
                <p className="text-sm text-neutral-600">
                  Aucune modalité n&apos;a encore été fixée pour ce dossier.
                </p>
              )}

              {manquementsBloquants.length > 0 && (
                <div className="mt-5 border-l-2 border-amber-300 pl-4">
                  <p className="text-sm font-medium text-neutral-900">
                    À corriger
                  </p>
                  <ul className="mt-2 space-y-2 text-sm text-neutral-600">
                    {manquementsBloquants.map(manquement => (
                      <li key={`${manquement.fondement}-${manquement.constat}`}>
                        {manquement.constat}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {modifiable && chargementInformations && (
                <p className="mt-5 text-xs text-neutral-500" role="status">
                  Vérification des informations envoyées…
                </p>
              )}

              {modifiable && erreurInformations && (
                <div className="mt-5 border-l-2 border-amber-300 pl-4">
                  <p className="text-sm text-neutral-700">
                    Le suivi des informations n&apos;est pas disponible.
                  </p>
                  <button
                    type="button"
                    onClick={() => void chargerInformations()}
                    className="mt-2 min-h-11 text-sm font-medium text-neutral-700 underline decoration-neutral-300 underline-offset-4"
                  >
                    Réessayer
                  </button>
                </div>
              )}

              {modifiable && !erreurInformations && aUnIncidentInformation && (
                <div className="mt-5 border-l-2 border-amber-300 pl-4">
                  <p className="text-sm font-medium text-neutral-900">
                    Information des bénéficiaires
                  </p>
                  <p className="mt-1 text-sm text-neutral-600">
                    {informationsEnEchec > 0 &&
                      `${informationsEnEchec} en échec`}
                    {informationsEnEchec > 0 && informationsEnAttente > 0
                      ? ' · '
                      : ''}
                    {informationsEnAttente > 0 &&
                      `${informationsEnAttente} en attente`}
                  </p>
                  {informationsEnEchec > 0 &&
                    informations?.repriseAutorisee && (
                      <button
                        type="button"
                        disabled={repriseEnCours}
                        onClick={() => void reprendreInformations()}
                        className="mt-2 min-h-11 text-sm font-medium text-neutral-700 underline decoration-neutral-300 underline-offset-4 disabled:opacity-40"
                      >
                        {repriseEnCours ? 'Reprise…' : 'Reprendre'}
                      </button>
                    )}
                </div>
              )}

              {modifiable && confirmationReprise && !aUnIncidentInformation && (
                <p className="mt-5 text-xs text-emerald-700" role="status">
                  Informations reprises.
                </p>
              )}

              {modifiable && (
                <button
                  type="button"
                  onClick={commencerEdition}
                  className="focus-visible:ring-primary-600 mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-neutral-700 hover:bg-neutral-100 hover:text-neutral-950 focus-visible:ring-2 focus-visible:outline-none"
                >
                  <PencilLine className="h-4 w-4" aria-hidden="true" />
                  {etat?.modalites ? 'Modifier' : 'Définir les modalités'}
                </button>
              )}
            </div>
          )}
          {!chargement && !erreur && (
            <p className="mt-5 text-xs text-neutral-400">
              Fondement : article R2312-12 du Code du travail.
            </p>
          )}
        </div>
      </details>
    </section>
  );
}
